/**
 * CoachingHintsPanel — In-session coaching guidance panel.
 *
 * EPIC-06-02 AC-2, AC-3, AC-4, AC-5, AC-6
 *
 * Renders a collapsible panel with three sections:
 *   1. About this customer — who you are talking to and their communication style.
 *   2. Scoring focus areas — one tip per scored competency.
 *   3. Common mistakes to avoid — three bullets for this scenario type.
 *
 * ## Usage
 * ```tsx
 * <CoachingHintsPanel hints={getCoachingHints(scenarioId)} isOpen={showHints} />
 * ```
 *
 * ## Placement
 * Rendered above the transcript panel in SessionPage.  The toggle button that
 * controls `isOpen` lives in the controls bar (see SessionPage.tsx).
 *
 * ## Visibility (AC-7)
 * Hiding when not connected is handled by the parent (SessionPage only renders
 * the toggle button and this component when `connectionStatus === 'connected'`).
 *
 * ## i18n (AC-6)
 * All section headings and competency labels are passed through `t()` from
 * react-i18next.  Competency names are looked up via
 * `summary.competency.<key>` (reuses existing translation keys).
 */

import { useTranslation } from 'react-i18next';
import type { CoachingHints } from '../data/coaching-hints';
import styles from './CoachingHintsPanel.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CoachingHintsPanelProps {
  /** Hint data for the current session's scenario. `null` renders a fallback. */
  hints: CoachingHints | null;
  /** Whether the panel is currently expanded. Controlled by SessionPage. */
  isOpen: boolean;
}

// ---------------------------------------------------------------------------
// Competency → i18n key mapping
// Reuses the `summary.competency.*` keys already defined in en.json.
// ---------------------------------------------------------------------------

const COMPETENCY_I18N_KEYS: Record<string, string> = {
  communication:       'summary.competency.communication',
  product_knowledge:   'summary.competency.productKnowledge',
  compliance:          'summary.competency.compliance',
  objection_handling:  'summary.competency.objectionHandling',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Collapsible coaching hints panel (EPIC-06-02).
 *
 * @param hints   - Hint data for the scenario; renders a fallback when null.
 * @param isOpen  - Controlled: true = expanded, false = hidden.
 */
export function CoachingHintsPanel({ hints, isOpen }: CoachingHintsPanelProps): JSX.Element | null {
  const { t } = useTranslation('training');

  // AC-2: hidden when not open
  if (!isOpen) return null;

  return (
    <aside
      className={styles.panel}
      data-testid="coaching-hints-panel"
      aria-label={t('session.hints.panelLabel')}
    >
      {hints === null ? (
        // AC-4 / null fallback
        <p className={styles.noData} data-testid="hints-no-data">
          {t('session.hints.noData')}
        </p>
      ) : (
        <>
          {/* ── Section 1: About this customer ── */}
          <section
            className={styles.section}
            data-testid="hints-section-about"
          >
            <h3
              className={styles.sectionHeading}
              data-i18n="session.hints.aboutTitle"
            >
              {t('session.hints.aboutTitle')}
            </h3>
            <p className={styles.aboutText}>{hints.aboutPersona}</p>
          </section>

          {/* ── Section 2: Scoring focus areas ── */}
          <section
            className={styles.section}
            data-testid="hints-section-scoring"
          >
            <h3
              className={styles.sectionHeading}
              data-i18n="session.hints.scoringTitle"
            >
              {t('session.hints.scoringTitle')}
            </h3>
            <ul className={styles.scoringList}>
              {hints.scoringFocus.map((item) => (
                <li key={item.competency} className={styles.scoringItem}>
                  <span
                    className={styles.competencyLabel}
                    data-i18n={COMPETENCY_I18N_KEYS[item.competency] ?? item.competency}
                  >
                    {t(COMPETENCY_I18N_KEYS[item.competency] ?? item.competency)}
                  </span>
                  <span className={styles.competencyTip}>{item.tip}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* ── Section 3: Common mistakes ── */}
          <section
            className={styles.section}
            data-testid="hints-section-mistakes"
          >
            <h3
              className={styles.sectionHeading}
              data-i18n="session.hints.mistakesTitle"
            >
              {t('session.hints.mistakesTitle')}
            </h3>
            <ul className={styles.mistakesList}>
              {hints.commonMistakes.map((mistake, idx) => (
                <li key={idx} className={styles.mistakeItem}>
                  {mistake}
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </aside>
  );
}
